import { useLocation } from "react-router-dom";
import { useClientAthlete } from "../../../context/ClientAthleteContext";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import ChatInboxHub from "../common/ChatInboxHub";
import SupportConversationPage from "../../common/SupportChat/SupportConversationPage";
import ChatConversationPage from "../common/ChatConversationPage";
import { initials } from "../common/ChatThread";
import md from "../common/manageDesktop.module.css";

const CLIENT_CHAT_BASE = "/app/chat";

function ClientMemberChatPage({ userId, name, desktop = false }) {
  const displayName = name || "Чат";
  return (
    <ChatConversationPage
      title={displayName}
      subtitle="Личные сообщения"
      backTo={desktop ? undefined : CLIENT_CHAT_BASE}
      threadType="member"
      peerUserId={userId}
      mode="client"
      avatarLabel={initials(displayName)}
      fullscreen={!desktop}
      hideBack={desktop}
    />
  );
}

function ClientGroupChatPage({ groupId, name, desktop = false }) {
  const { athleteQueryParam } = useClientAthlete();
  return (
    <ChatConversationPage
      title={name || "Группа"}
      subtitle="Чат тренировочной группы"
      backTo={desktop ? undefined : CLIENT_CHAT_BASE}
      threadType="group"
      groupId={groupId}
      mode="client"
      athleteQueryParam={athleteQueryParam}
      avatarLabel="👥"
      fullscreen={!desktop}
      hideBack={desktop}
    />
  );
}

function ClientClubChatPage({ desktop = false }) {
  return (
    <ChatConversationPage
      title="Чат клуба"
      subtitle="Общий чат всех участников клуба"
      backTo={desktop ? undefined : CLIENT_CHAT_BASE}
      threadType="club"
      mode="client"
      avatarLabel="🏟"
      fullscreen={!desktop}
      hideBack={desktop}
    />
  );
}

function ClientPersonalChatPage({ desktop = false }) {
  const { athleteQueryParam } = useClientAthlete();
  return (
    <ChatConversationPage
      title="Команда клуба"
      subtitle="Сообщения администрации и тренерам"
      backTo={desktop ? undefined : CLIENT_CHAT_BASE}
      threadType="direct"
      mode="client"
      athleteQueryParam={athleteQueryParam}
      avatarLabel="К"
      fullscreen={!desktop}
      hideBack={desktop}
    />
  );
}

function ClientSupportChatPage({ desktop = false }) {
  return (
    <SupportConversationPage
      mode="club"
      shell="client"
      title="Поддержка Бай.Спорт"
      subtitle="Команда платформы"
      backTo={desktop ? undefined : CLIENT_CHAT_BASE}
      fullscreen={!desktop}
      hideBack={desktop}
    />
  );
}

function ChatDesktopPlaceholder() {
  return (
    <div className={md.desktopChatPlaceholder}>
      <p className={md.desktopChatPlaceholderTitle}>Выберите чат</p>
      <p className={md.desktopChatPlaceholderHint}>
        Слева — команда клуба, группы, личные чаты и поддержка
      </p>
    </div>
  );
}

function resolveDesktopConversation(path, state) {
  if (path === `${CLIENT_CHAT_BASE}/support`) {
    return <ClientSupportChatPage desktop />;
  }
  if (path === `${CLIENT_CHAT_BASE}/club`) {
    return <ClientClubChatPage desktop />;
  }
  if (path === `${CLIENT_CHAT_BASE}/personal`) {
    return <ClientPersonalChatPage desktop />;
  }

  const groupMatch = path.match(/\/chat\/group\/([^/]+)$/);
  if (groupMatch) {
    return (
      <ClientGroupChatPage
        groupId={groupMatch[1]}
        name={state?.name}
        desktop
      />
    );
  }

  const memberMatch = path.match(/\/chat\/member\/([^/]+)$/);
  if (memberMatch) {
    return (
      <ClientMemberChatPage
        userId={memberMatch[1]}
        name={state?.name}
        desktop
      />
    );
  }

  return null;
}

function ClientChatDesktopLayout({ pathname, state }) {
  const conversation = resolveDesktopConversation(pathname, state);

  return (
    <div className={md.desktopChatSplit}>
      <aside className={md.desktopChatSidebar} aria-label="Список чатов">
        <ChatInboxHub portal="client" embedded />
      </aside>
      <section className={md.desktopChatMain} aria-label="Переписка">
        {conversation || <ChatDesktopPlaceholder />}
      </section>
    </div>
  );
}

export default function ClientChatRoutes() {
  const { pathname, state } = useLocation();
  const path = pathname.replace(/\/$/, "");
  const isMobile = useIsMobile(768);

  if (!isMobile) {
    return <ClientChatDesktopLayout pathname={path} state={state} />;
  }

  if (path === `${CLIENT_CHAT_BASE}/club`) {
    return <ClientClubChatPage />;
  }
  if (path === `${CLIENT_CHAT_BASE}/personal`) {
    return <ClientPersonalChatPage />;
  }
  if (path === `${CLIENT_CHAT_BASE}/support`) {
    return <ClientSupportChatPage />;
  }

  const groupMatch = path.match(/\/chat\/group\/([^/]+)$/);
  if (groupMatch) {
    return (
      <ClientGroupChatPage
        groupId={groupMatch[1]}
        name={state?.name}
      />
    );
  }

  const memberMatch = path.match(/\/chat\/member\/([^/]+)$/);
  if (memberMatch) {
    return (
      <ClientMemberChatPage
        userId={memberMatch[1]}
        name={state?.name}
      />
    );
  }

  return <ChatInboxHub portal="client" />;
}
